import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { useEffect, useRef } from 'react'

interface SingleOtpInputProps {
   index: number
   isInputNum?: boolean
   inputStyle: any
   focusStyle: any
   focus: any
   disabledStyle: any
   errorStyle: any
   isFocus?: boolean
   isDisabled?: boolean
   hasErrored?: boolean
   placeholder?: string | null
   separator?: any
   isLastChild: boolean
   isInputSecure?: boolean
   shouldAutoFocus: boolean
   value: string
   className?: string
   onBlur?: (event: React.SyntheticEvent) => void
   disabled?: boolean
   onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
   onKeyDown: (event: React.KeyboardEvent) => void
   onInput: (event: any) => void
   onPaste: (event: React.ClipboardEvent) => void
   onFocus: (event: React.FocusEvent<HTMLInputElement>) => void
   otp?: string[]
   activeInput: number
}

// Doesn't really check if it's a style Object
// Basic implementation to check if it's not a string
// of classNames and is an Object
// TODO: Better implementation
export const isStyleObject = (obj: any) => typeof obj === 'object'

const SingleOtpInput = (props: SingleOtpInputProps) => {
   const input = useRef<HTMLInputElement>(null)

   // Focus on first render
   // Only when shouldAutoFocus is true

   useEffect(() => {
      const { focus, shouldAutoFocus } = props
      const { current: inputEl } = input

      if (inputEl && focus && shouldAutoFocus) {
         inputEl.focus()
      }
   }, [])

   useEffect(() => {
      const { focus } = props
      const { current: inputEl } = input

      if (focus && inputEl && focus) {
         inputEl.focus()
         inputEl.select()
      }
   }, [props.focus, input])

   const getClasses = (...classes: any[]) =>
      classes.filter(c => !isStyleObject(c) && c !== false).join(' ')

   const getType = (): string => {
      const { isInputSecure, isInputNum } = props

      if (isInputSecure) {
         return 'password'
      }

      if (isInputNum) {
         return 'tel'
      }

      return 'number'
   }

   const {
      placeholder,
      separator,
      isLastChild,
      inputStyle,
      focus,
      hasErrored,
      errorStyle,
      focusStyle,
      disabledStyle,
      shouldAutoFocus,
      isInputNum,
      index,
      value,
      className,
      isInputSecure,
      onBlur,
      isFocus,
      isDisabled,
      otp,
      activeInput,
      onKeyDown,
      ...rest
   } = props

   // Using theme from shared-web common
   const otpInputsTheme = {
      otpInputEnabledBorderColor: 'border-primary-600'
   }

   const disabled = otp !== undefined && activeInput < index && !otp[index - 1]
   const hasValue = value !== undefined || (!isFocus && value !== undefined)

   const handleKeyDown = (e: React.KeyboardEvent) => {
      const allowedKeys = [
         'Backspace',
         'Delete',
         'ArrowLeft',
         'ArrowRight',
         'Tab',
         'Enter'
      ]
      if (
         !/[0-9]/.test(e.key) &&
         !allowedKeys.includes(e.key) &&
         !e.ctrlKey &&
         !e.metaKey
      ) {
         e.preventDefault()
      }
      props.onKeyDown(e)
   }

   return (
      <input
         aria-label={`${index === 0 ? 'Please enter verification code. ' : ''}${
            isInputNum ? 'Digit' : 'Character'
         } ${index + 1}`}
         autoComplete='off'
         style={Object.assign(
            {
               textAlign: 'center'
            },
            isStyleObject(inputStyle) && inputStyle,
            focus && isStyleObject(focusStyle) && focusStyle,
            isDisabled && isStyleObject(disabledStyle) && disabledStyle,
            hasErrored && !isFocus && isStyleObject(errorStyle) && errorStyle
         )}
         placeholder={placeholder || ''}
         className={cn(
            getClasses(
               inputStyle,
               focus && focusStyle,
               isDisabled && disabledStyle,
               hasErrored && !isFocus && errorStyle
            ),
            { [otpInputsTheme.otpInputEnabledBorderColor]: hasValue },
            { 'border-[2px]': hasValue },
            'sixboxes-input'
         )}
         type={getType()}
         inputMode='numeric'
         pattern={'[0-9]*'}
         maxLength={1}
         onKeyDown={handleKeyDown}
         ref={input}
         disabled={disabled}
         value={value ?? ''}
         onBlur={onBlur}
         {...rest}
      />
   )
}

export default observer(SingleOtpInput)
